import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const WhatsAppIcon = ({ size = 20, className }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);
import { Plus, Trash2, Edit2, Wifi, WifiOff, QrCode, RefreshCw, Loader2, AlertCircle, RotateCcw, PlugZap, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { getInstances, createInstance, updateInstance, deleteInstance } from '../lib/api';
import { cn, getConnectionStatusLabel, getConnectionStatusColor } from '../lib/utils';
import { toast } from 'sonner';

const asDataUrl = (qr) => {
  if (!qr) return null;
  if (typeof qr !== 'string') return null;
  if (qr.startsWith('data:image')) return qr;
  if (qr.includes('base64,')) return qr;
  return `data:image/png;base64,${qr}`;
};

const backendBase = () => process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const InstancesEvolution = ({ currentUser }) => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const instancesRef = useRef([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstance, setEditingInstance] = useState(null);
  const [formData, setFormData] = useState({ name: '' });

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrImage, setQrImage] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const selectedInstanceRef = useRef(null);
  const qrImageRef = useRef(null);

  const [statusLoading, setStatusLoading] = useState({});
  const [restartLoading, setRestartLoading] = useState({});
  const [connectLoading, setConnectLoading] = useState({});
  const [disconnectLoading, setDisconnectLoading] = useState({});
  const [createLoading, setCreateLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchInstances = async () => {
    try {
      const response = await getInstances();
      setInstances(response.data || []);
    } catch (error) {
      console.error('Error fetching instances:', error);
      toast.error('Erro ao carregar instâncias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    instancesRef.current = instances || [];
  }, [instances]);

  useEffect(() => {
    selectedInstanceRef.current = selectedInstance;
  }, [selectedInstance]);

  useEffect(() => {
    qrImageRef.current = qrImage;
  }, [qrImage]);

  const getNextInstanceDisplayName = () => {
    const nextNumber = (instances?.length || 0) + 1;
    return `instancia${nextNumber}`;
  };

  useEffect(() => {
    fetchInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh: keep the page updated without user clicks
  useEffect(() => {
    const interval = setInterval(() => {
      fetchInstances();
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh QR while QR dialog is open (no manual "Atualizar" button)
  useEffect(() => {
    if (!qrDialogOpen || !selectedInstance) return;
    if (qrImage) return;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const url = `${backendBase()}/api/instances/${selectedInstance.id}/qrcode`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        if (res.data?.success) {
          if (res.data?.connected) {
            // Connected => QR should disappear
            setQrDialogOpen(false);
            setQrImage(null);
            setQrLoading(false);
            setSelectedInstance(null);
            return;
          }
          const qr = res.data?.qrcode;
          if (qr) {
            setQrImage(asDataUrl(qr));
            setQrLoading(false);
          }
        }
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) {
          setTimeout(tick, 2000);
        }
      }
    };

    setQrLoading(true);
    tick();
    return () => {
      cancelled = true;
    };
  }, [qrDialogOpen, qrImage, selectedInstance]);

  // Auto-refresh statuses so cards update without user clicks
  useEffect(() => {
    const interval = setInterval(async () => {
      const list = instancesRef.current || [];
      const toCheck = list.filter((i) => i?.id && i.status !== 'connected');
      if (toCheck.length === 0) return;

      for (const inst of toCheck) {
        try {
          const url = `${backendBase()}/api/instances/${inst.id}/status`;
          const res = await axios.get(url, { headers: getAuthHeaders() });
          if (res.data?.success && res.data?.status) {
            setInstances((prev) =>
              (prev || []).map((x) => (x.id === inst.id ? { ...x, status: res.data.status } : x))
            );
          }
          // If QR dialog is open for this instance and it becomes connected, close it
          if (
            res.data?.success &&
            res.data?.status === 'connected' &&
            selectedInstanceRef.current?.id === inst.id
          ) {
            // Connected => QR should disappear
            setQrDialogOpen(false);
            setQrImage(null);
            setQrLoading(false);
            setSelectedInstance(null);
          }
        } catch (e) {
          // ignore
        }
      }
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = (instance = null) => {
    if (instance) {
      setEditingInstance(instance);
      setFormData({
        name: instance.name || '',
      });
    } else {
      setEditingInstance(null);
      setFormData({
        // Auto naming: instancia2, instancia3, ...
        name: getNextInstanceDisplayName(),
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setCreateLoading(true);
      const displayName = formData.name || getNextInstanceDisplayName();

      const dataToSend = {
        name: displayName,
      };

      if (editingInstance) {
        await updateInstance(editingInstance.id, dataToSend);
        toast.success('Instância atualizada');
        setDialogOpen(false);
        fetchInstances();
        return;
      }

      const created = await createInstance(dataToSend);
      const createdInstance = created?.data;
      if (!createdInstance?.id) {
        toast.success('Instância criada');
        setDialogOpen(false);
        await fetchInstances();
        return;
      }

      // Wait for QR code to be ready (or become connected) before leaving the create dialog
      const qrResult = await waitForQrOrConnected(createdInstance);
      await fetchInstances();
      setDialogOpen(false);

      if (qrResult?.connected) {
        toast.success('Instância conectada');
        return;
      }

      if (qrResult?.qrcode) {
        setSelectedInstance(createdInstance);
        setQrDialogOpen(true);
        setQrLoading(false);
        setQrImage(asDataUrl(qrResult.qrcode));
        toast.success('QR Code gerado');
      } else {
        toast.success('Instância criada');
      }
    } catch (error) {
      console.error('Error saving instance:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar instância');
    } finally {
      setCreateLoading(false);
    }
  };

  const waitForQrOrConnected = async (instance, timeoutMs = 60000, intervalMs = 2000) => {
    const startedAt = Date.now();
    const qrcodeUrl = `${backendBase()}/api/instances/${instance.id}/qrcode`;
    const statusUrl = `${backendBase()}/api/instances/${instance.id}/status`;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - startedAt > timeoutMs) {
        return { qrcode: null, connected: false };
      }

      try {
        const qrRes = await axios.get(qrcodeUrl, { headers: getAuthHeaders() });
        if (qrRes.data?.success) {
          if (qrRes.data?.connected) return { qrcode: null, connected: true };
          const qr = qrRes.data?.qrcode;
          if (qr) return { qrcode: qr, connected: false };
        }
      } catch (e) {
        // ignore
      }

      try {
        const stRes = await axios.get(statusUrl, { headers: getAuthHeaders() });
        if (stRes.data?.success && stRes.data?.status === 'connected') {
          return { qrcode: null, connected: true };
        }
      } catch (e) {
        // ignore
      }

      await new Promise((r) => setTimeout(r, intervalMs));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir esta instância?')) return;
    try {
      await deleteInstance(id);
      toast.success('Instância excluída');
      fetchInstances();
    } catch (error) {
      console.error('Error deleting instance:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir instância');
    }
  };

  const handleGetQRCode = async (instance) => {
    setSelectedInstance(instance);
    setQrDialogOpen(true);
    setQrLoading(true);
    setQrImage(null);

    try {
      const url = `${backendBase()}/api/instances/${instance.id}/qrcode`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });

      if (response.data?.success) {
        const qr = response.data.qrcode;
        setQrImage(asDataUrl(qr));
        if (!qr && response.data?.connected) {
          toast.success('Instância já conectada');
        }
      } else {
        toast.error(response.data?.detail || 'Erro ao buscar QR Code');
      }
    } catch (error) {
      console.error('Error fetching QR code:', error);
      toast.error(error.response?.data?.detail || 'Erro ao buscar QR Code');
    } finally {
      setQrLoading(false);
    }
  };

  const handleCheckStatus = async (instance) => {
    setStatusLoading((prev) => ({ ...prev, [instance.id]: true }));
    try {
      const url = `${backendBase()}/api/instances/${instance.id}/status`;
      const response = await axios.get(url, { headers: getAuthHeaders() });

      if (response.data?.success) {
        await fetchInstances();
        toast.success('Status atualizado');
        // If connected, close QR modal (QR should disappear)
        if (response.data?.status === 'connected' && selectedInstance?.id === instance.id) {
          setQrDialogOpen(false);
          setQrImage(null);
          setQrLoading(false);
        }
      } else {
        toast.error(response.data?.detail || 'Erro ao verificar status');
      }
    } catch (error) {
      console.error('Status error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao verificar status');
    } finally {
      setStatusLoading((prev) => ({ ...prev, [instance.id]: false }));
    }
  };

  const pollStatusUntilConnected = async (instance, timeoutMs = 60000, intervalMs = 2500) => {
    const url = `${backendBase()}/api/instances/${instance.id}/status`;

    const startedAt = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - startedAt > timeoutMs) {
        toast.error('Não conectou a tempo. Você pode tentar novamente ou abrir o QR Code.');
        await fetchInstances();
        return;
      }

      try {
        const response = await axios.get(url, { headers: getAuthHeaders() });
        if (response.data?.success) {
          const nextStatus = response.data?.status;
          setInstances((prev) =>
            prev.map((i) => (i.id === instance.id ? { ...i, status: nextStatus } : i))
          );
          if (nextStatus === 'connected') {
            await fetchInstances();
            return;
          }
        }
      } catch (e) {
        // ignore intermittent errors during polling
      }

      await new Promise((r) => setTimeout(r, intervalMs));
    }
  };

  const handleRestart = async (instance) => {
    setRestartLoading((prev) => ({ ...prev, [instance.id]: true }));
    try {
      const url = `${backendBase()}/api/instances/${instance.id}/restart`;
      const response = await axios.post(url, null, { headers: getAuthHeaders() });

      if (response.data?.success) {
        toast.success('Reiniciando... monitorando status');
        await fetchInstances();
        await pollStatusUntilConnected(instance);
      } else {
        toast.error(response.data?.detail || 'Erro ao reiniciar');
      }
    } catch (error) {
      console.error('Restart error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reiniciar');
    } finally {
      setRestartLoading((prev) => ({ ...prev, [instance.id]: false }));
    }
  };

  const handleConnect = async (instance) => {
    setConnectLoading((prev) => ({ ...prev, [instance.id]: true }));
    try {
      const url = `${backendBase()}/api/instances/${instance.id}/connect`;
      const response = await axios.post(url, null, { headers: getAuthHeaders() });

      if (response.data?.success) {
        const qr = response.data?.qrcode;
        toast.success('Gerando QR Code... monitorando status');
        await fetchInstances();

        setSelectedInstance(instance);
        setQrDialogOpen(true);
        setQrLoading(false);
        setQrImage(asDataUrl(qr));

        await pollStatusUntilConnected(instance);
      } else {
        toast.error(response.data?.detail || 'Erro ao conectar');
      }
    } catch (error) {
      console.error('Connect error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao conectar');
    } finally {
      setConnectLoading((prev) => ({ ...prev, [instance.id]: false }));
    }
  };

  const handleDisconnect = async (instance) => {
    if (!confirm('Tem certeza que deseja desconectar esta instância?')) return;
    setDisconnectLoading((prev) => ({ ...prev, [instance.id]: true }));
    try {
      const url = `${backendBase()}/api/instances/${instance.id}/disconnect`;
      const response = await axios.delete(url, { headers: getAuthHeaders() });

      if (response.data?.success) {
        toast.success('Instância desconectada');
        setQrDialogOpen(false);
        setQrImage(null);
        setQrLoading(false);
        await fetchInstances();
      } else {
        toast.error(response.data?.detail || 'Erro ao desconectar');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao desconectar');
    } finally {
      setDisconnectLoading((prev) => ({ ...prev, [instance.id]: false }));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Instâncias</h1>
          <p className="text-muted-foreground">
            Crie sua instância WhatsApp (Evolution). O nome na Evolution é derivado do nome da empresa no painel do Dev.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus size={16} />
          Nova Instância
        </Button>
      </div>

      <Card>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : instances.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              Nenhuma instância cadastrada
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {instances.map((instance) => (
                <Card
                  key={instance.id}
                  className={cn(
                    "border-border transition-colors",
                    "hover:border-primary/30"
                  )}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-base truncate">{instance.name}</CardTitle>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "flex items-center gap-1.5 px-2 py-1",
                          getConnectionStatusColor(instance.status)
                        )}
                      >
                        <WhatsAppIcon size={14} />
                        {getConnectionStatusLabel(instance.status)}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      {instance.status !== 'connected' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleGetQRCode(instance)}
                          className="justify-center"
                        >
                          <QrCode size={16} className="mr-2" />
                          QR Code
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled
                          className="justify-center opacity-70"
                          title="Instância conectada"
                        >
                          <QrCode size={16} className="mr-2" />
                          Conectado
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCheckStatus(instance)}
                        disabled={!!statusLoading[instance.id]}
                        className="justify-center"
                      >
                        <RefreshCw size={16} className={cn("mr-2", statusLoading[instance.id] && "animate-spin")} />
                        Status
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {instance.status === 'disconnected' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleConnect(instance)}
                          disabled={!!connectLoading[instance.id]}
                          className="justify-center"
                          title="Conectar (gera QR Code)"
                        >
                          <PlugZap size={16} className={cn("mr-2", connectLoading[instance.id] && "animate-spin")} />
                          Conectar
                        </Button>
                      ) : null}
                      {instance.status === 'connected' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDisconnect(instance)}
                          disabled={!!disconnectLoading[instance.id]}
                          className="justify-center text-destructive hover:text-destructive"
                          title="Desconectar (logout)"
                        >
                          <LogOut size={16} className={cn("mr-2", disconnectLoading[instance.id] && "animate-spin")} />
                          Desconectar
                        </Button>
                      ) : null}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRestart(instance)}
                        disabled={!!restartLoading[instance.id]}
                        className="justify-center"
                        title="Reiniciar instância"
                      >
                        <RotateCcw size={16} className={cn("mr-2", restartLoading[instance.id] && "animate-spin")} />
                        Reiniciar
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDialog(instance)}
                        className="justify-center"
                      >
                        <Edit2 size={16} className="mr-2" />
                        Editar
                      </Button>
                      {(currentUser?.role === 'developer' || currentUser?.role === 'admin') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(instance.id)}
                          className="justify-center text-destructive hover:text-destructive"
                        >
                          <Trash2 size={16} className="mr-2" />
                          Excluir
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Instance Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono">
              {editingInstance ? 'Editar Instância' : 'Nova Instância'}
            </DialogTitle>
            <DialogDescription>
              Ao criar, o backend chamará a Evolution usando as configurações da sua empresa.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome da Instância (no painel)</Label>
              <Input
                placeholder="Ex: instancia2"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={createLoading}>
              {createLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingInstance ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <QrCode size={20} className="text-primary" />
              Conectar WhatsApp
            </DialogTitle>
            <DialogDescription>
              {selectedInstance?.name} - Escaneie o QR Code com o WhatsApp
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center justify-center py-6">
            {qrLoading ? (
              <div className="w-64 h-64 flex items-center justify-center bg-secondary rounded-lg">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
            ) : qrImage ? (
              <div className="p-4 bg-white rounded-lg">
                <img
                  src={qrImage}
                  alt="QR Code"
                  className="w-64 h-64 object-contain"
                />
              </div>
            ) : (
              <div className="w-64 h-64 flex flex-col items-center justify-center bg-secondary rounded-lg">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground text-center px-4">
                  {selectedInstance?.status === 'connected'
                    ? 'Instância já conectada.'
                    : 'Não foi possível carregar o QR Code. Verifique a configuração da Evolution.'}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setQrDialogOpen(false)} className="flex-1">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstancesEvolution;

